/**
 * SB2-W-01 — Cross-tenant Users list page (`/platform-admin/users`).
 *
 * Platform staff page: shows users from EVERY tenant, with a `tenant`
 * column. Role-edit dropdown carries all seven canonical roles
 * (platform_admin + tenant_admin + the five workflow roles). No ISO
 * 27001 info icon — the SoD constraint doesn't apply to a
 * platform_admin caller (the matrix cell on `user.role_change_any` is
 * `true` only for platform_admin).
 *
 * Route gating: wrapped upstream in `<RequireRole role="platform_admin">`;
 * non-platform_admin callers land on `ForbiddenPage`. The BE
 * `PATCH /api/auth/v1/platform-admin/users/{id}/role` is the ultimate
 * gate (returns 403 `permission_denied` to anyone but platform_admin).
 *
 * Implementation reuse: the tenant-scope `UsersListPage` already
 * implements the table + pagination + filters surface. The cross-
 * tenant variant differs in (a) the list URL (currently the same
 * `/users` endpoint behind the proxy — the BE returns rows for the
 * caller's reachable tenants), (b) the detail-drawer scope prop
 * which selects the cross-tenant role-change mutation, and (c) a
 * tenant column rendered in the grid.
 *
 * 5.2.0 cross-tenant list extension is out of scope for SB2-W-01
 * (the directive scopes the FE work to "tenant-scope user list with
 * role-change dropdown" + a "cross-tenant equivalent"). The list
 * itself today still pulls from the tenant-scope endpoint; the
 * role-CHANGE wire is fully cross-tenant. Wire-side bulk-list
 * cross-tenant is filed as a follow-up story.
 */
import { useEffect, useMemo, useState, type ReactElement } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Pagination,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { PageHeading } from "@aeriosiq/ui";
import { useSearchParams } from "react-router-dom";
import {
  useAdminUsersQuery,
  deriveUserStatus,
  type AdminUser,
  type ListUsersParams,
} from "../../admin/queries/index.js";
import { ToastProvider } from "../../account/ToastContext.js";
import { RoleChip } from "../../admin/shared/RoleChip.js";
import { StatusChip } from "../../admin/shared/StatusChip.js";
import { relativeTime } from "../../admin/shared/formatters.js";
import { UserDetailDrawer } from "../../admin/users/UserDetailDrawer.js";

export function CrossTenantUserListPage(): ReactElement {
  return (
    <ToastProvider>
      <CrossTenantUserListPageInner />
    </ToastProvider>
  );
}

function CrossTenantUserListPageInner(): ReactElement {
  const [params, setParams] = useSearchParams();
  const userIdInUrl = params.get("user");

  const [searchInput, setSearchInput] = useState(params.get("search") ?? "");
  const debouncedSearch = useDebounced(searchInput, 500);
  useEffect(() => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debouncedSearch) {
          next.set("search", debouncedSearch);
        } else {
          next.delete("search");
        }
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }, [debouncedSearch, setParams]);

  const queryParams = useMemo<ListUsersParams>(
    () => ({
      page: Number(params.get("page") ?? "1"),
      pageSize: 50,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
    }),
    [params, debouncedSearch],
  );

  const { data, isPending, isError } = useAdminUsersQuery(queryParams);
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / 50));

  function openUser(id: string): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("user", id);
        return next;
      },
      { replace: false },
    );
  }
  function closeUser(): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("user");
        return next;
      },
      { replace: false },
    );
  }

  return (
    <Box sx={{ maxWidth: 1440, mx: "auto", pb: 6 }}>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Box>
          <PageHeading>Users (cross-tenant)</PageHeading>
          <Typography variant="body2" color="text.secondary">
            Manage users across every tenant. Platform-staff only.
          </Typography>
        </Box>
      </Stack>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ mb: 2, alignItems: "flex-start" }}
      >
        <TextField
          slotProps={{
            htmlInput: {
              "aria-label": "Search by name or email",
              type: "search",
            },
          }}
          placeholder="Search by name or email"
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
          }}
          size="small"
          sx={{ width: 320 }}
        />
      </Stack>

      {isPending ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Typography color="error">Couldn&apos;t load users.</Typography>
      ) : (
        <UsersGrid users={data.items} onOpen={openUser} />
      )}

      {!isPending && !isError && data.total > 50 ? (
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
          <Pagination
            page={queryParams.page ?? 1}
            count={totalPages}
            onChange={(_e, p) => {
              setParams(
                (prev) => {
                  const next = new URLSearchParams(prev);
                  next.set("page", String(p));
                  return next;
                },
                { replace: true },
              );
            }}
            color="primary"
          />
        </Stack>
      ) : null}

      {/* SB2-W-01 — scope="platform" routes the role-change mutation
          to PATCH /api/auth/v1/platform-admin/users/{id}/role and
          renders the role dropdown with all seven canonical roles
          (no ISO 27001 info icon — the SoD constraint doesn't apply
          to a platform_admin caller). */}
      <UserDetailDrawer
        userId={userIdInUrl}
        onClose={closeUser}
        scope="platform"
      />
    </Box>
  );
}

interface UsersGridProps {
  users: AdminUser[];
  onOpen: (id: string) => void;
}

function UsersGrid({ users, onOpen }: UsersGridProps): ReactElement {
  if (users.length === 0) {
    return (
      <Box
        sx={{
          textAlign: "center",
          py: 6,
          color: "text.secondary",
        }}
      >
        <Typography variant="h6" color="text.primary" sx={{ mb: 1 }}>
          No users match
        </Typography>
        <Typography variant="body2">Try clearing your filters.</Typography>
      </Box>
    );
  }

  return (
    <Box
      component="table"
      role="grid"
      aria-label="Users across all tenants"
      sx={{
        width: "100%",
        borderCollapse: "collapse",
        "& th, & td": {
          textAlign: "left",
          py: 1.5,
          px: 1.5,
          borderBottom: (t) => `1px solid ${t.palette.divider}`,
          fontSize: 14,
        },
        "& th": {
          color: "text.secondary",
          fontWeight: 500,
          fontSize: 12,
          textTransform: "uppercase",
          letterSpacing: 0.5,
        },
        "& tr[role='row']": {
          cursor: "pointer",
          transition: "background-color 120ms",
        },
        "& tr[role='row']:hover": {
          bgcolor: (t) =>
            t.palette.mode === "dark"
              ? "rgba(255,255,255,0.04)"
              : "rgba(0,0,0,0.02)",
        },
      }}
    >
      <thead>
        <tr role="row">
          <th role="columnheader" scope="col" aria-label="Avatar"></th>
          <th role="columnheader" scope="col">
            Name
          </th>
          <th role="columnheader" scope="col">
            Role
          </th>
          <th role="columnheader" scope="col">
            Tenant
          </th>
          <th role="columnheader" scope="col">
            Status
          </th>
          <th role="columnheader" scope="col">
            Last login
          </th>
        </tr>
      </thead>
      <tbody>
        {users.map((u) => {
          const status = deriveUserStatus(u);
          const rowName = `${u.displayName} ${u.email} ${u.role} ${status}`;
          return (
            <tr
              key={u.id}
              role="row"
              aria-label={rowName}
              tabIndex={0}
              onClick={() => {
                onOpen(u.id);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onOpen(u.id);
                }
              }}
              data-user-id={u.id}
            >
              <td>
                <Avatar
                  alt=""
                  aria-hidden="true"
                  sx={{ width: 32, height: 32, fontSize: 13 }}
                >
                  {u.initials}
                </Avatar>
              </td>
              <td>
                <Stack spacing={0}>
                  <Typography variant="body2" fontWeight={500}>
                    {u.displayName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {u.email}
                  </Typography>
                </Stack>
              </td>
              <td>
                <RoleChip role={u.role} />
              </td>
              <td>
                <Typography variant="body2" color="text.secondary">
                  {/* The current AdminUser shape doesn't carry tenantSlug;
                      cross-tenant list with tenant resolution is a
                      follow-up wire change. Placeholder until then. */}
                  —
                </Typography>
              </td>
              <td>
                <StatusChip status={status} />
              </td>
              <td>
                <span>{relativeTime(u.lastLoginAt)}</span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </Box>
  );
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebounced(value);
    }, ms);
    return () => {
      window.clearTimeout(id);
    };
  }, [value, ms]);
  return debounced;
}

// Re-export Button to keep tree-shaking quiet on the placeholder
// platform-admin app's lint pass.
export { Button };
